// components/Admin/CrewManager.tsx
//
// Host surface for CREWS — groups of presenters who share quizzes. A presenter
// can found crews, belong to many, and (as owner) add/remove members from the
// presenter directory; members may leave. Admins see and manage every crew.
// All reads are one-time get()s (crews + presenterProfiles are tiny host-only
// nodes) — no live listeners, per the bandwidth-lean rules.
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/Auth/auth-context';
import { Crew, PresenterProfile } from '../../types/presenter.types';
import {
  createCrew,
  deleteCrew,
  leaveCrew,
  listCrews,
  listPresenterDirectory,
  setCrewMember,
} from '../../services/presenter';

export const CrewManager = () => {
  const { user, userInfo } = useAuth();
  const isAdmin = userInfo?.role === 'admin';

  const [crews, setCrews] = useState<Record<string, Crew>>({});
  const [directory, setDirectory] = useState<Array<{ uid: string } & PresenterProfile>>([]);
  const [loaded, setLoaded] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  // Per-crew "add member" search text, keyed by crewId.
  const [memberSearch, setMemberSearch] = useState<Record<string, string>>({});

  const reload = async () => {
    try {
      const [c, d] = await Promise.all([listCrews(), listPresenterDirectory()]);
      setCrews(c);
      setDirectory(d);
    } catch {
      toast.error('Could not load crews — are you signed in as a host?');
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nameOf = useMemo(() => {
    const byUid = Object.fromEntries(directory.map((p) => [p.uid, p]));
    return (uid: string) => byUid[uid]?.name || `Presenter ${uid.slice(0, 6)}…`;
  }, [directory]);

  const visibleCrews = Object.entries(crews).filter(
    ([, crew]) => isAdmin || (user && crew.members?.[user])
  );

  const handleCreate = async () => {
    if (!user || !newName.trim()) return;
    setBusy(true);
    try {
      await createCrew(newName, user);
      setNewName('');
      await reload();
      toast.success('Crew created.');
    } catch {
      toast.error('Could not create the crew.');
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async (crewId: string, uid: string) => {
    try {
      await setCrewMember(crewId, uid, 'member');
      setMemberSearch((s) => ({ ...s, [crewId]: '' }));
      await reload();
    } catch {
      toast.error('Add rejected — only the crew owner can add members.');
    }
  };

  const handleRemove = async (crewId: string, uid: string) => {
    try {
      await setCrewMember(crewId, uid, null);
      await reload();
    } catch {
      toast.error('Remove rejected — only the crew owner can remove members.');
    }
  };

  const handleLeave = async (crewId: string) => {
    if (!user) return;
    try {
      await leaveCrew(crewId, user);
      await reload();
      toast.success('You left the crew.');
    } catch {
      toast.error('Could not leave the crew.');
    }
  };

  const handleDelete = async (crewId: string, crewName: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete crew "${crewName}"? Quizzes shared with it stop being shared.`)) return;
    try {
      await deleteCrew(crewId);
      await reload();
      toast.success('Crew deleted.');
    } catch {
      toast.error('Delete rejected — only the crew owner can delete it.');
    }
  };

  const crewCard = ([crewId, crew]: [string, Crew]) => {
    const myRole = user ? crew.members?.[user] : undefined;
    const canManage = isAdmin || myRole === 'owner';
    const memberEntries = Object.entries(crew.members ?? {});
    const q = (memberSearch[crewId] ?? '').trim().toLowerCase();
    const candidates = q
      ? directory.filter(
          (p) => !crew.members?.[p.uid] && p.name?.toLowerCase().includes(q)
        )
      : [];

    return (
      <div key={crewId} className="card p-5">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-bold flex-1 truncate">{crew.name}</h3>
          {myRole === 'owner' && <span className="chip bg-accent/30 text-ink shrink-0">OWNER</span>}
          {canManage ? (
            <button
              onClick={() => handleDelete(crewId, crew.name)}
              className="btn text-sm text-tile-red bg-tile-red/10 hover:bg-tile-red/20 shrink-0"
            >
              Delete
            </button>
          ) : (
            myRole && (
              <button
                onClick={() => handleLeave(crewId)}
                className="btn text-sm text-ink bg-ink/10 hover:bg-ink/15 shrink-0"
              >
                Leave
              </button>
            )
          )}
        </div>

        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mt-4 mb-2">
          Members ({memberEntries.length})
        </p>
        <div className="flex flex-wrap gap-2">
          {memberEntries.map(([uid, role]) => (
            <span key={uid} className="chip bg-ink/10 text-ink inline-flex items-center gap-1.5">
              {nameOf(uid)}
              {role === 'owner' && <span className="text-tile-gold font-bold" title="Crew owner">★</span>}
              {canManage && role !== 'owner' && (
                <button
                  onClick={() => handleRemove(crewId, uid)}
                  className="text-ink/40 hover:text-tile-red font-bold"
                  title="Remove from crew"
                  aria-label={`Remove ${nameOf(uid)}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>

        {canManage && (
          <div className="mt-3">
            <input
              type="text"
              placeholder="Add a presenter by name…"
              value={memberSearch[crewId] ?? ''}
              onChange={(e) => setMemberSearch((s) => ({ ...s, [crewId]: e.target.value }))}
              className="w-full p-2.5 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none text-sm"
            />
            {candidates.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {candidates.slice(0, 8).map((p) => (
                  <button
                    key={p.uid}
                    onClick={() => handleAdd(crewId, p.uid)}
                    className="chip bg-tile-green/15 text-ink hover:bg-tile-green/30"
                  >
                    + {p.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <h1 className="text-2xl font-extrabold font-display tracking-tight mb-1">Crews</h1>
      <p className="text-sm text-slate-500 mb-4">
        A crew is a group of presenters who share quizzes: share one of your quizzes with a crew
        and every member can host it (they can never edit it). You can be in any number of crews.
      </p>

      <div className="card p-4 flex gap-2 mb-6">
        <input
          type="text"
          placeholder="New crew name…"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          className="flex-1 p-2.5 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none"
        />
        <button onClick={handleCreate} disabled={busy || !newName.trim()} className="btn-primary text-sm shrink-0">
          Create crew
        </button>
      </div>

      {!loaded && <p className="text-slate-400 text-center py-10">Loading crews…</p>}
      {loaded && visibleCrews.length === 0 && (
        <p className="text-slate-400 text-center py-10">
          {isAdmin ? 'No crews exist yet.' : "You're not in a crew yet — create one, or ask a crew owner to add you."}
        </p>
      )}
      <div className="space-y-3">{visibleCrews.map(crewCard)}</div>
    </div>
  );
};
